/**
 * useInvestorAccess Hooks
 * Company-side hooks for managing investor fund access and per-assessment sharing.
 */

import { useContext } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/lib/api-client";
import { TenantContext } from "@/contexts/TenantContext";

/** Safe tenant access that returns null instead of throwing when outside TenantProvider. */
function useSafeTenant() {
  const ctx = useContext(TenantContext);
  return ctx?.currentTenant ?? null;
}
export interface PortfolioLinkDetail {
  id: string;
  investorTenantId: string;
  investorTenantName: string;
  isActive: boolean;
  addedBy: string;
  createdAt: string;
}

export interface ReceivedInvitationDetail {
  id: string;
  investorTenantName: string;
  invitedByEmail: string;
  status: "pending" | "accepted" | "declined" | "expired";
  createdAt: string;
  expiresAt: string;
}

/**
 * Fetch linked investor funds for the current tenant.
 */
export function usePortfolioLinks() {
  const currentTenant = useSafeTenant();

  return useQuery<PortfolioLinkDetail[], Error>({
    queryKey: ["portfolio-links", currentTenant?.id],
    queryFn: async () => {
      if (!currentTenant) throw new Error("No tenant selected");

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const url = `/tenants/${currentTenant.id}/portfolio-links` as any;
      const { data, error } = await apiClient.GET(url);

      if (error || !data) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        throw new Error(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (error as any)?.message || "Failed to fetch portfolio links",
        );
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (data as any).links ?? (data as PortfolioLinkDetail[]);
    },
    enabled: !!currentTenant,
    staleTime: 60 * 1000,
  });
}

/**
 * Fetch pending invitations received by the current tenant from investor funds.
 */
export function useReceivedInvitations() {
  const currentTenant = useSafeTenant();

  return useQuery<ReceivedInvitationDetail[], Error>({
    queryKey: ["received-invitations", currentTenant?.id],
    queryFn: async () => {
      if (!currentTenant) throw new Error("No tenant selected");

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const url = `/tenants/${currentTenant.id}/invitations` as any;
      const { data, error } = await apiClient.GET(url);

      if (error || !data) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        throw new Error(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (error as any)?.message || "Failed to fetch invitations",
        );
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (data as any).invitations ?? (data as ReceivedInvitationDetail[]);
    },
    enabled: !!currentTenant,
    staleTime: 30 * 1000,
  });
}

/**
 * Accept an invitation from an investor fund.
 */
export function useAcceptInvitation() {
  const currentTenant = useSafeTenant();
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: async (invitationId: string) => {
      if (!currentTenant) throw new Error("No tenant selected");

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const url =
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        `/tenants/${currentTenant.id}/invitations/${invitationId}/accept` as any;
      const { error } = await apiClient.POST(url);

      if (error) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        throw new Error(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (error as any)?.message || "Failed to accept invitation",
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["received-invitations", currentTenant?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["portfolio-links", currentTenant?.id],
      });
    },
  });
}

/**
 * Decline an invitation from an investor fund.
 */
export function useDeclineInvitation() {
  const currentTenant = useSafeTenant();
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: async (invitationId: string) => {
      if (!currentTenant) throw new Error("No tenant selected");

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const url =
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        `/tenants/${currentTenant.id}/invitations/${invitationId}/decline` as any;
      const { error } = await apiClient.POST(url);

      if (error) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        throw new Error(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (error as any)?.message || "Failed to decline invitation",
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["received-invitations", currentTenant?.id],
      });
    },
  });
}

/**
 * Remove an investor fund's access to this tenant.
 */
export function useRemovePortfolioLink() {
  const currentTenant = useSafeTenant();
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: async (investorTenantId: string) => {
      if (!currentTenant) throw new Error("No tenant selected");

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const url =
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        `/tenants/${currentTenant.id}/portfolio-links/${investorTenantId}` as any;
      const { error } = await apiClient.DELETE(url);

      if (error) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        throw new Error(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (error as any)?.message || "Failed to remove portfolio link",
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["portfolio-links", currentTenant?.id],
      });
    },
  });
}

/**
 * Toggle investor visibility for a specific assessment.
 */
export function useSetInvestorVisibility() {
  const currentTenant = useSafeTenant();
  const queryClient = useQueryClient();

  return useMutation<
    void,
    Error,
    { assessmentId: string; sharedWithInvestors: boolean }
  >({
    mutationFn: async ({ assessmentId, sharedWithInvestors }) => {
      if (!currentTenant) throw new Error("No tenant selected");

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const url =
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        `/tenants/${currentTenant.id}/assessments/${assessmentId}/investor-visibility` as any;
      const { error } = await apiClient.PATCH(url, {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        body: { sharedWithInvestors } as any,
      });

      if (error) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        throw new Error(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (error as any)?.message || "Failed to update investor visibility",
        );
      }
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["assessment", currentTenant?.id, variables.assessmentId],
      });
    },
  });
}
